import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import { ApolloError, useMutation } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { observer } from 'mobx-react'

import { ButtonGroup, CardBody, Heading } from 'Components/_theme'
import { Form } from 'Components/Form/Form'
import { FormOptionalMessage } from 'Components/FormOptionalMessage/FormOptionalMessage'
import { ModalDevicePresentationBlock } from 'Components/ModalDevicePresentationBlock/ModalDevicePresentationBlock'
import { ModalErrorState } from 'Components/ModalErrorState/ModalErrorState'

import { replaceBrokenDeviceSummarySchema } from 'Modals/ReplaceBrokenDeviceModal/ReplaceBrokenDeviceModal.schema'

import { useStore } from 'Stores/index'

import { parseShippingAddressBeforeSubmit } from 'Utilities'

import { countryISOCodes } from 'Constants/constants'
import { E2E_REPLACE_BROKEN_DEVICE_SUBMIT } from 'Constants/e2e'
import {
  mutationReplaceBrokenDevice,
  mutationReplaceBrokenDeviceVariables,
} from 'Constants/graphql/mutations/__generated__/mutationReplaceBrokenDevice'

import { useActiveUser } from 'Portal/src/hooks'

import { MUTATION_REPLACE_BROKEN_DEVICE } from '../../../../constants/graphql/mutations/MutationReplaceBrokenDevice'
import { REPLACE_BROKEN_DEVICE_MODAL_SUMMARY_FORM } from '../../ReplaceBrokenDeviceModal.constants'
import {
  ReplaceBrokenDeviceModalStepsEnum,
  ReplaceBrokenDeviceSummaryProps,
  ReplaceBrokenDeviceSummarySchema,
} from '../../ReplaceBrokenDeviceModal.types'

export const ReplaceBrokenDeviceSummary = observer(
  (props: ReplaceBrokenDeviceSummaryProps) => {
    const { rental, shippingFormData, onSetCurrentStep, onSubmit } = props
    const { userStore } = useStore()
    const { user } = userStore
    const { activeEmployee, activeOrganisation } = useActiveUser()
    const organisationID =
      activeEmployee?.organisation?.id || activeOrganisation!.id
    const intl = useIntl()
    const [submitError, setSubmitError] = useState<ApolloError | null>(null)
    const formMethods = useForm<ReplaceBrokenDeviceSummarySchema>({
      defaultValues: {
        message: '',
      },
      resolver: zodResolver(replaceBrokenDeviceSummarySchema),
    })
    const [submitReplaceBrokenDevice, { loading: isLoading }] = useMutation<
      mutationReplaceBrokenDevice,
      mutationReplaceBrokenDeviceVariables
    >(MUTATION_REPLACE_BROKEN_DEVICE, {
      onCompleted: () => {
        onSubmit()
      },
      onError: error => {
        setSubmitError(error)
      },
    })

    const handlePrev = () => {
      onSetCurrentStep(ReplaceBrokenDeviceModalStepsEnum.SHIPPING)
    }

    const handleSubmit = formMethods.handleSubmit(data => {
      submitReplaceBrokenDevice({
        variables: {
          input: {
            message: data.message,
            organisationID,
            rentalID: rental.id,
            shippingAddress: parseShippingAddressBeforeSubmit(shippingFormData),
            userID: user.userId?.toString() ?? '0',
          },
        },
      })
    })

    const hasError = !!submitError
    const isUnitedStatesAddressType =
      shippingFormData?.country?.value === countryISOCodes.US

    return (
      <CardBody>
        {!hasError ? (
          <>
            <Heading as={6} className="mb-2" size={2}>
              <FormattedMessage id="Summary" />{' '}
            </Heading>

            <ModalDevicePresentationBlock className="mt-6" rental={rental}>
              {shippingFormData && (
                <>
                  <h6>
                    <FormattedMessage id="Shipping_address" />
                  </h6>

                  <p>
                    {shippingFormData.name}
                    <br />
                    {shippingFormData.nameAddition}
                    <br />
                    {isUnitedStatesAddressType
                      ? shippingFormData.address
                      : `${shippingFormData.street} / ${shippingFormData.streetNumber}`}
                    <br />
                    {shippingFormData.zipcode} / {shippingFormData.city}
                    <br />
                    {shippingFormData.country?.label}
                  </p>
                </>
              )}
            </ModalDevicePresentationBlock>

            <p className="small">
              <FormattedMessage id="ExchangeDeviceModal_shipping_label_info" />
            </p>

            <div className="mt-6">
              <FormProvider {...formMethods}>
                <Form
                  id={REPLACE_BROKEN_DEVICE_MODAL_SUMMARY_FORM}
                  onSubmit={handleSubmit}
                >
                  <FormOptionalMessage />
                </Form>
              </FormProvider>
            </div>
          </>
        ) : (
          <ModalErrorState error={submitError} />
        )}

        <ButtonGroup
          leftButton={{
            onClick: handlePrev,
            text: intl.formatMessage({ id: 'Back' }),
          }}
          rightButton={
            !hasError
              ? {
                  'data-e2e': E2E_REPLACE_BROKEN_DEVICE_SUBMIT,
                  disabled: isLoading,
                  form: REPLACE_BROKEN_DEVICE_MODAL_SUMMARY_FORM,
                  text: intl.formatMessage({ id: 'Replace_broken_device' }),
                  type: 'submit',
                }
              : undefined
          }
        />
      </CardBody>
    )
  },
)
